/**
 * Certificados da pessoa no painel (spec 011, secao 4; spec 013, item 5): a equipe sobe o PDF ou a imagem, ve a lista e baixa. Apagar e do ADMIN e
 * e reversivel no banco (some da lista do trader). O que foi emitido no sistema antigo continua la.
 */
import { Aviso, Botao, CampoArquivo, CampoData, Campo, CampoTextoLongo, Carregando, Secao, Tabela, Vazio, formatarBytes, formatarDataHora, formatarDia, hojeEmSaoPaulo } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { useSessao } from "../sessao";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePagina } from "../pagina";

type Certificado = { id: string; titulo: string; emitido_em: string; observacao: string | null; arquivo_nome: string; tamanho: number; criado_em: string };

/**
 * Ver como o trader (spec 012): abre a area dele numa aba nova, so leitura, por 15 minutos, com faixa na tela. A
 * sessao do trader nao e tocada, e a trilha guarda quem olhou. Só ADMIN.
 */
export function VerComoOTrader({ pessoaId, nome }: { pessoaId: string; nome: string }) {
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [feito, setFeito] = useState<string>();
  const historico = useQuery({ queryKey: ["acessos-assistidos", pessoaId], queryFn: () => ler<{ itens: { id: string; operador: string; motivo: string | null; criado_em: string; encerrado_em: string | null }[] }>(`/v1/admin/pessoas/${pessoaId}/acessos-assistidos`) });
  if (!pode("SUPORTE")) return null;
  return (
    <Secao rotulo="Suporte" titulo="Ver como o trader" acoes={pode("ADMIN") ? <Botao tamanho="pequeno" variante="secundario" onClick={() => { setMotivo(""); setFeito(undefined); setAberto(true); }}>Abrir a área dele</Botao> : undefined}>
      <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>
        Abre a área de {nome.split(" ")[0]} numa aba nova, somente leitura, por 15 minutos. A sessão dele não muda e fica registrado quem olhou.
      </span>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {Boolean(historico.data?.itens.length) && (
        <Tabela
          linhas={historico.data!.itens}
          chave={(a) => a.id}
          vazio={<Vazio titulo="Ninguém olhou ainda">Nada registrado.</Vazio>}
          colunas={[
            { chave: "quando", rotulo: "Quando", celula: (a) => <span className="mtc-mono">{formatarDataHora(a.criado_em)}</span> },
            { chave: "quem", rotulo: "Quem", celula: (a) => <>{a.operador}{a.motivo && <span className="mtc-tabela__apoio">{a.motivo}</span>}</> },
            { chave: "fim", rotulo: "Encerrado", celula: (a) => (a.encerrado_em ? <span className="mtc-mono">{formatarDataHora(a.encerrado_em)}</span> : <span className="mtc-texto-2">no prazo</span>) },
          ]}
        />
      )}
      <DialogoDeAcao
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Ver como o trader"
        descricao={`A área de ${nome} abre numa aba nova, somente leitura, por 15 minutos. Pra agir, use o painel.`}
        botao="Abrir a área"
        aoEnviar={async () => {
          const r = await postar<{ link: string }>(`/v1/admin/pessoas/${pessoaId}/acesso-assistido`, motivo.trim() ? { motivo: motivo.trim() } : {});
          window.open(r.link, "_blank", "noopener");
        }}
        aoConcluir={() => { setFeito("Abri numa aba nova. Ela expira em 15 minutos, ou quando você clicar em Encerrar lá."); void historico.refetch(); }}
      >
        <CampoTextoLongo rotulo="Por que você está olhando (opcional)" valor={motivo} aoMudar={setMotivo} maxLength={300} dica="Fica na trilha, junto com o seu nome." />
      </DialogoDeAcao>
    </Secao>
  );
}

export function CertificadosDaPessoa({ pessoaId, nome }: { pessoaId: string; nome: string }) {
  const pag = usePagina("pagina_certificados");
  const { ler, arquivo, apagar: apagarCertificado } = useApi();
  const { enviar } = useSessao();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const [novo, setNovo] = useState(false);
  const [apagar, setApagar] = useState<Certificado | null>(null);
  const [baixando, setBaixando] = useState<string>();
  const [erro, setErro] = useState<string>();
  const [f, setF] = useState<{ titulo: string; emitido: string; observacao: string; pdf: File | null }>({ titulo: "", emitido: hojeEmSaoPaulo(), observacao: "", pdf: null });
  const q = useQuery({ queryKey: ["certificados", pessoaId], queryFn: () => ler<{ itens: Certificado[] }>(`/v1/admin/pessoas/${pessoaId}/certificados`) });
  const admin = pode("ADMIN");
  const baixar = async (c: Certificado) => {
    setErro(undefined);
    setBaixando(c.id);
    try {
      const blob = await arquivo(`/v1/admin/certificados/${c.id}/arquivo`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = c.arquivo_nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setErro(erroDe(e));
    } finally {
      setBaixando(undefined);
    }
  };
  return (
    <Secao rotulo="Certificados" titulo="Certificados da pessoa" acoes={admin ? <Botao tamanho="pequeno" onClick={() => { setF({ titulo: "", emitido: hojeEmSaoPaulo(), observacao: "", pdf: null }); setNovo(true); }}>Enviar certificado</Botao> : undefined}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {q.isLoading ? <Carregando compacto /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <Tabela
          paginacao={pag}
          linhas={q.data?.itens ?? []}
          chave={(c) => c.id}
          vazio={<Vazio titulo="Nenhum certificado">{admin ? "Envie o PDF ou a imagem quando emitir um. O trader baixa na área dele." : "Nada emitido por aqui ainda."}</Vazio>}
          colunas={[
            { chave: "titulo", rotulo: "Certificado", celula: (c) => <><strong>{c.titulo}</strong><span className="mtc-tabela__apoio">{c.arquivo_nome} · {formatarBytes(c.tamanho)}{c.observacao ? `. ${c.observacao}` : ""}</span></> },
            { chave: "emitido", rotulo: "Emitido em", celula: (c) => <span className="mtc-mono">{formatarDia(c.emitido_em)}</span> },
            { chave: "enviado", rotulo: "Enviado em", celula: (c) => <span className="mtc-mono">{formatarDataHora(c.criado_em)}</span> },
            {
              chave: "acoes",
              rotulo: "",
              celula: (c) => (
                <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
                  <Botao tamanho="pequeno" variante="secundario" carregando={baixando === c.id} onClick={() => void baixar(c)}>Baixar</Botao>
                  {admin && <Botao tamanho="pequeno" variante="fantasma" onClick={() => setApagar(c)}>Apagar</Botao>}
                </div>
              ),
            },
          ]}
        />
      )}
      <DialogoDeAcao
        aberto={novo}
        aoFechar={() => setNovo(false)}
        titulo="Enviar certificado"
        descricao={`O arquivo vai pra área de ${nome.split(" ")[0]}, que pode baixar quando quiser.`}
        botao="Enviar"
        podeEnviar={f.titulo.trim().length >= 2 && Boolean(f.pdf)}
        aoEnviar={() => {
          const form = new FormData();
          form.append("titulo", f.titulo.trim());
          form.append("emitido_em", f.emitido);
          if (f.observacao.trim()) form.append("observacao", f.observacao.trim());
          form.append("arquivo", f.pdf!);
          return enviar(`/v1/admin/pessoas/${pessoaId}/certificados`, form);
        }}
        aoConcluir={() => void qc.invalidateQueries({ queryKey: ["certificados", pessoaId] })}
      >
        <Campo rotulo="Título" valor={f.titulo} aoMudar={(v) => setF({ ...f, titulo: v })} placeholder="Ex.: Certificado de aprovação na Mesa Real" required />
        <CampoData rotulo="Emitido em" valor={f.emitido} aoMudar={(v) => setF({ ...f, emitido: v })} />
        <CampoTextoLongo rotulo="Observação (opcional)" valor={f.observacao} aoMudar={(v) => setF({ ...f, observacao: v })} maxLength={500} />
        <CampoArquivo rotulo="Arquivo (PDF, PNG ou JPG, até 10 MB)" arquivo={f.pdf} aoEscolher={(a) => setF({ ...f, pdf: a })} aceita="application/pdf,image/png,image/jpeg" />
      </DialogoDeAcao>
      <DialogoDeAcao
        aberto={Boolean(apagar)}
        aoFechar={() => setApagar(null)}
        titulo="Apagar certificado"
        descricao={apagar ? `${apagar.titulo} sai da área do trader. A trilha guarda quem apagou.` : undefined}
        botao="Apagar"
        variante="perigo"
        aoEnviar={() => apagarCertificado(`/v1/admin/certificados/${apagar!.id}`)}
        aoConcluir={() => { setApagar(null); void qc.invalidateQueries({ queryKey: ["certificados", pessoaId] }); }}
      />
    </Secao>
  );
}
